import {
    MDBBtn,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBRow,
} from "mdb-react-ui-kit";
import { FC, useEffect, useState } from "react";
import { useRouter } from "next/router";
// eslint-disable-next-line import/no-extraneous-dependencies
import TagManager from "react-gtm-module";
import { fileDownload } from "@/utils/analytics/tagmanager";
import CardBrochures from "../cards/CardBrochures";
import userInterfaceSvg from "../../../utils/svg-icon/userInterface";
import { useIsMD } from "../../../utils/hooks/useWindowSizes";
import styles from "../../../styles/home/Home.Brochures.module.scss";
import {
    card1Content,
    card2Content,
    card3Content,
    card4Content,
} from "../../../utils/mockData/homeMocks";
import Carousel from "../../common/carousel/Carousel";
import { TBreackpointsCarousel } from "../../../models/common/common";
import { BrochuresEntity, EBrochuresType } from "../../../models/home";

// Mappa gli enum ai tipi che arrivano dal CMS
const getBrochureTypeFromEnum = (type: EBrochuresType): string[] => {
    switch (type) {
        case EBrochuresType.ALL:
            return ["All the Tour", "All the Tours"];
        case EBrochuresType.ACROSSITALY:
            return ["Across Italy Brochure"];
        case EBrochuresType.INTOTHEREGION:
            return ["Into the Region Brochure"];
        case EBrochuresType.DOWNTHECITY:
            return [
                "Down the city Brochure",
                "Down the City Brochure",
                "Down The City Brochure",
                "Down the city",
                "Down the City",
            ];
        default:
            return [];
    }
};

// Trova una brochure con match esatto
const findExactMatch = (
    possibleTypes: string[],
    items: BrochuresEntity[]
): BrochuresEntity | undefined => {
    return items.find(item => possibleTypes.includes(item.type));
};

// Trova una brochure con match case-insensitive
const findCaseInsensitiveMatch = (
    possibleTypes: string[],
    items: BrochuresEntity[]
): BrochuresEntity | undefined => {
    return items.find(item => {
        const itemTypeLower = item.type.toLowerCase().trim();
        return possibleTypes.some(
            possibleType => possibleType.toLowerCase().trim() === itemTypeLower
        );
    });
};

// Trova una brochure con match parziale per "Down the city"
const findPartialMatch = (
    items: BrochuresEntity[]
): BrochuresEntity | undefined => {
    return items.find(
        item =>
            item.type.toLowerCase().includes("down") &&
            item.type.toLowerCase().includes("city")
    );
};

// Trova la brochure corrispondente al tipo
const findBrochureByType = (
    type: EBrochuresType,
    brochures: BrochuresEntity[]
): BrochuresEntity | undefined => {
    const possibleTypes = getBrochureTypeFromEnum(type);

    // Prova match esatto
    let brochure = findExactMatch(possibleTypes, brochures);

    // Se non trova, prova case-insensitive
    if (!brochure) {
        brochure = findCaseInsensitiveMatch(possibleTypes, brochures);
    }

    // Se ancora non trova e è "Down the city", prova match parziale
    if (!brochure && type === EBrochuresType.DOWNTHECITY) {
        brochure = findPartialMatch(brochures);
    }

    return brochure;
};

const BrochuresModal: FC<{
    show: boolean;
    setShow: (el: boolean) => void;
    brochures: BrochuresEntity[];
}> = ({ brochures, show, setShow }) => {
    const { CloseLine } = userInterfaceSvg;

    const toggleShow = () => setShow(!show);
    const [render, setRender] = useState(false);

    useEffect(() => setRender(true), []);

    const router = useRouter();

    // Crea e triggera il download del file
    const triggerDownload = (blob: Blob, fileName: string) => {
        const blobURL = window.URL.createObjectURL(blob);
        const aTag = document.createElement("a");
        aTag.href = blobURL;
        aTag.setAttribute("download", fileName);
        document.body.appendChild(aTag);
        aTag.click();
        aTag.remove();
        window.URL.revokeObjectURL(blobURL);

        TagManager.dataLayer(fileDownload(fileName, router.asPath));
    };

    // Gestisce gli errori del download
    const handleDownloadError = () => {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.error("Error downloading file");
        }
    };

    // Scarica il file dall'URL
    const downloadFileAtURL = (url: string | undefined, fileName: string) => {
        if (!url) {
            return;
        }

        // Usa fetch invece di axios per evitare l'interceptor che aggiunge X-Currency
        // L'header X-Currency può causare problemi con il download di file statici da CloudFront
        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => triggerDownload(blob, fileName))
            .catch(handleDownloadError);
    };

    const handleDownload = (type: EBrochuresType, title: string) => {
        const brochure = findBrochureByType(type, brochures);
        if (brochure?.brochure) {
            downloadFileAtURL(brochure.brochure, brochure.type || title);
        }
    };

    const cards = [
        {
            id: EBrochuresType.ALL,
            value: (
                <CardBrochures
                    content={card1Content}
                    type={EBrochuresType.ALL}
                    onDownload={handleDownload}
                />
            ),
        },
        {
            id: EBrochuresType.ACROSSITALY,
            value: (
                <CardBrochures
                    content={card2Content}
                    type={EBrochuresType.ACROSSITALY}
                    onDownload={handleDownload}
                />
            ),
        },
        {
            id: EBrochuresType.INTOTHEREGION,
            value: (
                <CardBrochures
                    content={card3Content}
                    type={EBrochuresType.INTOTHEREGION}
                    onDownload={handleDownload}
                />
            ),
        },
        {
            id: EBrochuresType.DOWNTHECITY,
            value: (
                <CardBrochures
                    content={card4Content}
                    type={EBrochuresType.DOWNTHECITY}
                    onDownload={handleDownload}
                />
            ),
        },
    ];

    const isMd = useIsMD();

    const breackPointCarousel: TBreackpointsCarousel = {
        0: {
            slidesPerView: 1,
            spaceBetween: 32,
        },
        576: {
            slidesPerView: 1.2,
            spaceBetween: 32,
        },
        768: {
            slidesPerView: 2.2,
            spaceBetween: 32,
        },
        992: {
            slidesPerView: 2.5,
            spaceBetween: 32,
        },
        1200: {
            slidesPerView: 3,
            spaceBetween: 32,
        },
        1400: {
            slidesPerView: 4,
            spaceBetween: 32,
        },
        1600: {
            slidesPerView: 4,
            spaceBetween: 32,
        },
    };

    return render ? (
        <MDBModal show={show} setShow={setShow} tabIndex="-1" staticBackdrop>
            <MDBModalDialog
                size="xl"
                className={`${styles.dialog} bg-image p-lg-5 rounded-9 p-0`}
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1478860409698-8707f313ee8b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80')",
                }}
            >
                <MDBModalContent
                    className={`${styles.overlayMask} mask rounded-9 d-flex align-items-center p-0`}
                >
                    <MDBModalHeader className="border border-0 d-flex justify-content-end  mt-5 p-0">
                        <MDBBtn
                            className={`${styles.buttonClose} position-absolute d-flex justify-content-center shadow-0`}
                            tag="a"
                            floating
                            onClick={toggleShow}
                        >
                            <CloseLine className="align-self-center" />
                        </MDBBtn>
                    </MDBModalHeader>
                    <MDBModalBody
                        className={`d-flex align-items-center ${
                            isMd && "w-100 p-0"
                        } ${styles.modalBody}`}
                    >
                        <MDBRow
                            className={`p-0 ${isMd && "h-100 w-100 flex-fill"}`}
                        >
                            {!isMd &&
                                cards.map(card => (
                                    <MDBCol
                                        className="mx-4 mx-lg-0"
                                        key={card.id}
                                        size={4}
                                        lg={3}
                                    >
                                        {card.value}
                                    </MDBCol>
                                ))}
                            {isMd && (
                                <MDBCol
                                    center
                                    size={12}
                                    className="h-100 d-flex justify-content-center align-items-center h-100 mx-3"
                                >
                                    <Carousel
                                        visibleSlides={2.2}
                                        breakpoints={breackPointCarousel}
                                    >
                                        {cards.map(el => (
                                            <div
                                                key={el.id}
                                                className={` ${styles.slideContainer}`}
                                            >
                                                {el.value}
                                            </div>
                                        ))}
                                    </Carousel>
                                </MDBCol>
                            )}
                        </MDBRow>
                    </MDBModalBody>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    ) : null;
};

export default BrochuresModal;
